// ============================================================
// THE TURN STRIP PAST ITS WIDTH (2026-10-21: "the top turn tracker does not
// scroll"; the canvas's TurnStrip artboard). The rails' order strip lays its
// seats left to right; with more seats than room it scrolls sideways - by
// the wheel, by ‹ › one seat a press, and to keep the target in view.
//
// The geometry, pure: a scroller's numbers and its seats' edges in, pixels
// to scroll out. AXIS-NEUTRAL (2026-10-22, plan muster): the muster scrolls
// the same way down its column, so a scroller is its position, content size
// and visible size along its axis, and an edge pair is client-space start
// and end along it (left and right across, top and bottom down) - the hook
// (`useStripOverflow`) reads the element for the axis it was given.
// ============================================================

interface Scroll {
  /** scrollLeft or scrollTop */
  pos: number;
  /** scrollWidth or scrollHeight */
  size: number;
  /** clientWidth or clientHeight */
  view: number;
}
interface Edges {
  start: number;
  end: number;
}

/** Rounding slack: a scroller a fraction of a pixel short of its end is at it. */
const SLACK = 1;
/**
 * The fade on a side that still hides seats (OrderRail.module.css's mask, 40 px): a seat brought into view is
 * brought clear of it, never left half faded. At the very end the browser clamps the scroll, so the last seat
 * sits flush with no fade over it.
 */
const FADE = 40;
/** A wheel counted in lines (Firefox's notch) or pages, as pixels. */
const LINE_PX = 16;

/** Whether seats are hidden, and whether the scroller sits at either end. */
export function overflowOf({ pos, size, view }: Scroll) {
  const overflow = size - view > SLACK;
  return { overflow, atStart: pos <= SLACK, atEnd: pos + view >= size - SLACK };
}

/**
 * How far a wheel turn scrolls the strip: an up-down wheel goes sideways (most mice have no other), a sideways one
 * as it is. Zero when the strip cannot move that way - the wheel is then the page's, never swallowed.
 */
/** A wheel event's pixels per unit: lines (Firefox's notch, mode 1), pages (mode 2), or pixels. */
const unitOf = (mode: number | undefined, s: Scroll) => (mode === 1 ? LINE_PX : mode === 2 ? s.view : 1);

export function wheelDelta(e: { deltaX: number; deltaY: number; deltaMode?: number }, s: Scroll): number {
  const delta = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * unitOf(e.deltaMode, s);
  const { overflow, atStart, atEnd } = overflowOf(s);
  if (!overflow || (delta < 0 && atStart) || (delta > 0 && atEnd)) return 0;
  return delta;
}

/**
 * The scroller's edges inside the fades: where a seat counts as fully seen. The fade is at most a quarter of the
 * scroller each side (the review): a short muster column at a crowded step must keep a clear band to bring a chip into.
 */
function clearOf(scroller: Edges) {
  const fade = Math.min(FADE, (scroller.end - scroller.start) / 4);
  return { start: scroller.start + fade, end: scroller.end - fade };
}

/**
 * One seat per press: how far to scroll to bring the next seat not clear of the fade clear of it, either way. Never
 * more than the clear band at once: a seat longer than it (a tall prompt over the map) is read a band at a time,
 * its middle never jumped past unseen.
 */
export function stepBy(seats: readonly Edges[], scroller: Edges, dir: 1 | -1): number {
  const clear = clearOf(scroller);
  const band = clear.end - clear.start;
  if (dir > 0) {
    const next = seats.find((s) => s.end > clear.end + SLACK);
    return next ? Math.min(next.end - clear.end, band) : 0;
  }
  const prev = [...seats].reverse().find((s) => s.start < clear.start - SLACK);
  return prev ? Math.max(prev.start - clear.start, -band) : 0;
}

/** How many seats lie wholly past the scroller's end: the count a "▾ N MORE" button gives. */
export function hiddenAfter(seats: readonly Edges[], scroller: Edges): number {
  return seats.filter((s) => s.start >= scroller.end - SLACK).length;
}

/** The least scroll that brings a seat clear of the fades: nothing when it already is. */
export function intoView(seat: Edges, scroller: Edges): number {
  const clear = clearOf(scroller);
  if (seat.start < clear.start) return seat.start - clear.start;
  if (seat.end > clear.end) return seat.end - clear.end;
  return 0;
}

/** The scroll that puts a seat in the middle (the acting seat, as the turn moves). */
export const centreOn = (seat: Edges, scroller: Edges): number =>
  (seat.start + seat.end) / 2 - (scroller.start + scroller.end) / 2;
